import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '@/store/authStore';
import { authApi } from '@/lib/api';
import { resolveApiMessage } from '@/lib/apiMessage';
import ReportAdminModal from '@/components/ReportAdminModal';
import CaptchaWidget, { captchaIsUsable } from '@/components/CaptchaWidget';
import { PLATFORM_PHONE, PLATFORM_PHONE_HREF } from '@/lib/platformContact';

type Mode = 'login' | 'forgot-password' | 'reset-password';

type PublicConfig = {
    branding: {
        name: string;
        display_name: string | null;
        logo_url: string | null;
    };
    captcha: {
        provider: 'none' | 'turnstile' | 'recaptcha' | 'hcaptcha';
        enabled: boolean;
        site_key: string;
        verify_on_admin_login: boolean;
        verify_on_student_login: boolean;
        supported: boolean;
    };
};

// window.turnstile and friends are declared once, in CaptchaWidget, which owns
// loading and rendering them. Two files declaring the same global with slightly
// different shapes is a type error waiting for whichever is edited second.

function redirectForRole(role: string): string {
    if (['institute_admin', 'principal', 'instructor', 'senior_instructor', 'admin_staff', 'accountant', 'hr', 'receptionist', 'counselor', 'operations'].includes(role)) return '/admin/institute-dashboard';
    return '/admin';
}

// A bento chip on the hero tile: translucent clay over the accent gradient, so
// it reads as an inset facet of the same material rather than a pasted-on box.

export default function LoginPage() {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const { login, error, isLoading, clearError } = useAuthStore();

    const resetToken = searchParams.get('reset_token') || '';
    const examClientOnly = searchParams.get('exam_client_only') === '1';
    const [mode, setMode] = useState<Mode>(resetToken ? 'reset-password' : 'login');
    const [publicConfig, setPublicConfig] = useState<PublicConfig>({
        branding: {
            name: 'Typing Institute',
            display_name: null,
            logo_url: null,
        },
        captcha: {
            provider: 'none',
            enabled: false,
            site_key: '',
            verify_on_admin_login: false,
            verify_on_student_login: false,
            supported: false,
        },
    });
    const [pageMessage, setPageMessage] = useState<string | null>(null);
    const [localError, setLocalError] = useState<string | null>(null);
    const [instituteFlagged, setInstituteFlagged] = useState<string | null>(null); // holds the flag message
    const [showPassword, setShowPassword] = useState(false);
    // Separate from the sign-in field: the reset screen has its own pair,
    // and they reveal together.
    const [showResetPassword, setShowResetPassword] = useState(false);
    const [identifier, setIdentifier] = useState('');
    const [password, setPassword] = useState('');
    const [forgotIdentifier, setForgotIdentifier] = useState('');
    const [resetPassword, setResetPassword] = useState('');
    const [resetConfirmPassword, setResetConfirmPassword] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [captchaToken, setCaptchaToken] = useState<string | null>(null);
    const [captchaResetKey, setCaptchaResetKey] = useState(0);

    // Server EXE: a slimmer, distraction-free login (no marketing panel) + a
    // direct "Report Admin" button, with the institute auto-detected from the
    // bound server license.
    const exeServer = (typeof window !== 'undefined' ? (window as any).examClient?.server : undefined);
    const isExe = !!exeServer;
    const [showReport, setShowReport] = useState(false);
    const [reportInstitute, setReportInstitute] = useState<{ name?: string; id?: string | null }>({});
    useEffect(() => {
        if (exeServer?.getInstituteInfo) {
            exeServer.getInstituteInfo()
                .then((r: any) => { if (r?.success) setReportInstitute({ name: r.data?.institute_name || undefined, id: r.data?.institute_id || null }); })
                .catch(() => {});
        }
    }, [exeServer]);

    const appTitle = publicConfig.branding.display_name || publicConfig.branding.name;
    // Any configured provider, not only Turnstile — reCAPTCHA and hCaptcha
    // were offered in settings and rendered nowhere.
    const isCaptchaEnabled = useMemo(() => captchaIsUsable(publicConfig.captcha), [publicConfig]);

    useEffect(() => {
        authApi
            .getPublicConfig()
            .then((data) => setPublicConfig(data))
            .catch(() => {});
    }, []);

    useEffect(() => {
        if (resetToken) {
            setMode('reset-password');
        }
    }, [resetToken]);

    useEffect(() => {
        if (examClientOnly) {
            setPageMessage('Student sign-in is available only in LAB EXE connected to the institute LAN server.');
        }
    }, [examClientOnly]);



    const activeError = localError || error;

    const resetUiState = (nextMode: Mode) => {
        clearError();
        setLocalError(null);
        setPageMessage(null);
        setMode(nextMode);
    };

    // Bumping this clears a solved challenge, which every provider requires
    // after a failed attempt — the token is single-use.
    const resetCaptchaIfNeeded = () => setCaptchaResetKey((n) => n + 1);

    const handleLoginSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        clearError();
        setLocalError(null);
        setPageMessage(null);
        setInstituteFlagged(null);

        if (isCaptchaEnabled && !captchaToken) {
            setLocalError('Complete the captcha verification to continue.');
            return;
        }

        try {
            await login({ email: identifier, password, captcha_token: captchaToken || undefined });
            const user = useAuthStore.getState().user;
            if (user) navigate(redirectForRole(user.role), { replace: true });
        } catch (err: any) {
            if (err?.error?.code === 'INSTITUTE_FLAGGED') {
                setInstituteFlagged(resolveApiMessage(err, 'Your institute has been suspended. Please contact the administrator.'));
            }
            resetCaptchaIfNeeded();
        }
    };

    const handleForgotPasswordSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLocalError(null);
        setPageMessage(null);
        setIsSubmitting(true);
        try {
            const result = await authApi.forgotPassword(forgotIdentifier);
            setPageMessage(result.message);
        } catch (err: any) {
            setLocalError(resolveApiMessage(err?.response?.data || err, 'Unable to send reset instructions'));
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleResetPasswordSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLocalError(null);
        setPageMessage(null);

        if (resetPassword !== resetConfirmPassword) {
            setLocalError('New password and confirm password do not match.');
            return;
        }

        setIsSubmitting(true);
        try {
            const result = await authApi.resetPassword(resetToken, resetPassword);
            setPageMessage(result.message);
            setResetPassword('');
            setResetConfirmPassword('');
            searchParams.delete('reset_token');
            setSearchParams(searchParams, { replace: true });
            setMode('login');
        } catch (err: any) {
            setLocalError(resolveApiMessage(err?.response?.data || err, 'Unable to reset password'));
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className={isExe ? 'mx-auto w-full max-w-md' : 'grid gap-6 lg:grid-cols-[1.15fr_0.95fr]'}>
            {/* Hero tile. The gradient is built from the Clay Studio accent vars
                rather than hard-coded teal, so it stays in step with the theme
                (and with dark mode) instead of drifting from the rest of the app. */}
            {!isExe && (
            <section
                className="relative overflow-hidden rounded-[2rem] p-8 text-white"
                style={{
                    backgroundImage:
                        'linear-gradient(150deg, var(--clay-accent) 0%, var(--clay-accent-deep) 52%, #2f2560 100%)',
                    boxShadow: 'var(--clay-shadow-raised)',
                }}
            >
                {/* Soft clay "kiln" lights — a bright highlight top-right and a
                    cooler bloom bottom-left, matching the body canvas treatment. */}
                <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(255,255,255,0.22),transparent_38%),radial-gradient(circle_at_bottom_left,rgba(255,255,255,0.12),transparent_34%)]" />
                <div className="relative flex h-full flex-col justify-center gap-6">
                    <div className="space-y-8">
                        <div className="flex items-center gap-4">
                            {publicConfig.branding.logo_url ? (
                                <img
                                    src={publicConfig.branding.logo_url}
                                    alt={appTitle}
                                    className="h-16 w-16 rounded-2xl bg-white/15 object-contain p-2 ring-1 ring-inset ring-white/25"
                                    onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                                />
                            ) : (
                                <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-white/15 ring-1 ring-inset ring-white/25">
                                    <svg className="h-8 w-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M4 6h16M4 12h16M4 18h10" />
                                    </svg>
                                </div>
                            )}
                            <div>
                                <h1 className="font-serif text-3xl font-bold tracking-tight">{appTitle}</h1>
                            </div>
                        </div>
                        {/* One line, not a product pitch. Someone signing in already
                            knows what this is; the old panel explained captcha,
                            password reset and every role before they could reach
                            the password box. */}
                        <p className="max-w-md text-xl font-medium leading-snug text-white/85">
                            Welcome back.
                        </p>
                    </div>

                    <p className="text-sm text-white/60">
                        Trouble signing in? Contact your institute administrator.
                    </p>
                    {/* The institute first, E-Swift second: nearly every sign-in
                        problem is a password or an account their own admin can
                        fix. This is for the rest — including when the institute
                        itself is the thing that is down and there is nobody
                        else to ask. */}
                    <p className="text-sm text-white/60">
                        Still stuck? Call E-Swift on{' '}
                        <a href={PLATFORM_PHONE_HREF} className="font-medium text-white/80 underline underline-offset-2">
                            {PLATFORM_PHONE}
                        </a>.
                    </p>
                </div>
            </section>
            )}

            <section className="card rounded-[2rem] p-6 sm:p-8">
                {/* There is no mode tab bar: "Forgot password?" sits beside the
                    password box and switches modes, so a second control for the
                    same action was only more surface to read. The Server EXE
                    never offered password reset anyway, and still doesn't —
                    that link is hidden there. */}

                <div className="space-y-5">
                    <div>
                        <h2 className="font-serif text-2xl font-bold tracking-tight text-slate-900 dark:text-white">
                            {mode === 'login' && 'Sign in'}
                            {mode === 'forgot-password' && 'Reset your password'}
                            {mode === 'reset-password' && 'Set a new password'}
                        </h2>
                        {/* Say only what the person needs in order to act. The
                            sign-in line used to list four account types and where
                            students go instead — none of which helps someone type
                            their own password. Students are told on the sign-in
                            failure path, where it is actually useful. */}
                        <p className="mt-2 text-sm leading-6 text-slate-500 dark:text-slate-400">
                            {mode === 'login' && 'Enter your username or email and password.'}
                            {mode === 'forgot-password' && 'We’ll send reset instructions to your registered email.'}
                            {mode === 'reset-password' && 'Use at least eight characters.'}
                        </p>
                    </div>

                    {pageMessage && (
                        <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-300">
                            {pageMessage}
                        </div>
                    )}

                    {instituteFlagged && (
                        <div className="rounded-2xl border border-amber-300 bg-amber-50 p-4 dark:border-amber-800 dark:bg-amber-950/40">
                            <div className="flex items-start gap-3">
                                <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-amber-200 text-amber-700">
                                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                </div>
                                <div>
                                    <p className="font-semibold text-amber-800 dark:text-amber-200">Institute Suspended</p>
                                    <p className="mt-1 text-sm text-amber-700 dark:text-amber-300">{instituteFlagged}</p>
                                    <p className="mt-2 text-xs text-amber-600 font-medium">Please contact the super administrator for assistance.</p>
                                </div>
                            </div>
                        </div>
                    )}

                    {activeError && !instituteFlagged && (
                        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:border-rose-900 dark:bg-rose-950/40 dark:text-rose-300">
                            {activeError}
                        </div>
                    )}

                    {mode === 'login' && (
                        <form onSubmit={handleLoginSubmit} className="space-y-4">
                            <div>
                                <label htmlFor="identifier" className="mb-2 block text-sm font-semibold text-slate-700 dark:text-slate-300">Username or email</label>
                                <input
                                    id="identifier"
                                    type="text"
                                    value={identifier}
                                    onChange={(e) => setIdentifier(e.target.value)}
                                    required
                                    autoFocus
                                    autoComplete="username"
                                    className="input-field"
                                                                    />
                            </div>
                            <div>
                                <div className="mb-2 flex items-center justify-between">
                                    <label htmlFor="password" className="block text-sm font-semibold text-slate-700 dark:text-slate-300">Password</label>
                                    {!isExe && (
                                        <button type="button" onClick={() => resetUiState('forgot-password')} className="text-xs font-semibold text-[var(--clay-accent)] hover:underline">
                                            Forgot password?
                                        </button>
                                    )}
                                </div>
                                <div className="relative">
                                    <input
                                        id="password"
                                        type={showPassword ? 'text' : 'password'}
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        required
                                        autoComplete="current-password"
                                        className="input-field pr-12"
                                                                            />
                                    <button type="button" onClick={() => setShowPassword((prev) => !prev)} className="absolute right-4 top-1/2 -translate-y-1/2 text-xs font-bold uppercase tracking-wider text-slate-500 hover:text-[var(--clay-accent)] dark:text-slate-400">
                                        {showPassword ? 'Hide' : 'Show'}
                                    </button>
                                </div>
                            </div>
                            <button
                                type="submit"
                                disabled={isLoading}
                                className="btn-primary w-full"
                            >
                                {isLoading ? 'Signing in...' : 'Sign In'}
                            </button>
                        </form>
                    )}

                    {isCaptchaEnabled && mode === 'login' && (
                        <div className="rounded-2xl bg-[var(--clay-well)] p-4 shadow-[var(--clay-shadow-pressed)]">
                            {/* The widget explains itself; a caption telling the
                                user that verification is required, while they are
                                looking at the verification box, is noise. */}
                            <CaptchaWidget
                                provider={publicConfig.captcha.provider}
                                siteKey={publicConfig.captcha.site_key}
                                onToken={setCaptchaToken}
                                resetKey={captchaResetKey}
                            />
                        </div>
                    )}

                    {mode === 'forgot-password' && (
                        <form onSubmit={handleForgotPasswordSubmit} className="space-y-4">
                            <div>
                                <label htmlFor="forgot-identifier" className="mb-2 block text-sm font-semibold text-slate-700 dark:text-slate-300">Username or email</label>
                                <input
                                    id="forgot-identifier"
                                    type="text"
                                    value={forgotIdentifier}
                                    onChange={(e) => setForgotIdentifier(e.target.value)}
                                    required
                                    autoFocus
                                    className="input-field"
                                                                    />
                            </div>
                            <button
                                type="submit"
                                disabled={isSubmitting}
                                className="btn-primary w-full"
                            >
                                {isSubmitting ? 'Sending instructions...' : 'Send Reset Instructions'}
                            </button>
                        </form>
                    )}

                    {mode === 'reset-password' && (
                        <form onSubmit={handleResetPasswordSubmit} className="space-y-4">
                            {/* One toggle reveals BOTH fields. Choosing a password
                                and confirming it is a single act — being able to
                                see the two together is the point, and it is one
                                control instead of two. */}
                            <div>
                                <div className="mb-2 flex items-center justify-between gap-3">
                                    <label htmlFor="reset-password" className="block text-sm font-semibold text-slate-700 dark:text-slate-300">New password</label>
                                    <button
                                        type="button"
                                        onClick={() => setShowResetPassword((prev) => !prev)}
                                        className="text-xs font-bold uppercase tracking-wider text-slate-500 hover:text-[var(--clay-accent)] dark:text-slate-400"
                                    >
                                        {showResetPassword ? 'Hide' : 'Show'}
                                    </button>
                                </div>
                                <input
                                    id="reset-password"
                                    type={showResetPassword ? 'text' : 'password'}
                                    value={resetPassword}
                                    onChange={(e) => setResetPassword(e.target.value)}
                                    required
                                    autoFocus
                                    autoComplete="new-password"
                                    className="input-field"
                                />
                            </div>
                            <div>
                                <label htmlFor="reset-confirm-password" className="mb-2 block text-sm font-semibold text-slate-700 dark:text-slate-300">Confirm password</label>
                                <input
                                    id="reset-confirm-password"
                                    type={showResetPassword ? 'text' : 'password'}
                                    value={resetConfirmPassword}
                                    onChange={(e) => setResetConfirmPassword(e.target.value)}
                                    required
                                    autoComplete="new-password"
                                    className="input-field"
                                />
                            </div>
                            <button
                                type="submit"
                                disabled={isSubmitting}
                                className="btn-primary w-full"
                            >
                                {isSubmitting ? 'Updating password...' : 'Reset Password'}
                            </button>
                        </form>
                    )}

                    {mode !== 'login' && mode !== 'reset-password' && (
                        <button type="button" onClick={() => resetUiState('login')} className="text-sm font-semibold text-slate-500 hover:text-[var(--clay-accent)] dark:text-slate-400">
                            Back to portal login
                        </button>
                    )}

                    {/* Direct line to the platform admin — works before sign-in. */}
                    <div className="mt-6 border-t border-[var(--clay-line)] pt-4 text-center">
                        <button
                            type="button"
                            onClick={() => setShowReport(true)}
                            className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M5.07 19h13.86c1.54 0 2.5-1.67 1.73-3L13.73 4a2 2 0 00-3.46 0L3.34 16c-.77 1.33.19 3 1.73 3z" /></svg>
                            Report a problem
                        </button>
                    </div>
                </div>
            </section>

            <ReportAdminModal
                open={showReport}
                onClose={() => setShowReport(false)}
                source="login"
                defaultInstituteName={reportInstitute.name}
                defaultInstituteId={reportInstitute.id ?? null}
            />
        </div>
    );
}
